/**
 * WorldCupSkeleton — Skeleton states for World Cup tab sections.
 * Each variant mirrors the structural layout of its actual screen.
 */

import React, { useMemo } from 'react';
import { View, useWindowDimensions } from 'react-native';
import { useTheme } from '../../../src/theme/useTheme';
import { Skeleton } from '../../ui/Skeleton/Skeleton';
import { getWorldCupSkeletonStyles } from './WorldCupSkeleton.styles';
import { BANNER_HEIGHT } from '../GroupHeroBanner/GroupHeroBanner.styles';

type SkeletonVariant = 'picks' | 'leaderboard' | 'groups' | 'groupDetail';

interface WorldCupSkeletonProps {
  variant: SkeletonVariant;
}

/**
 * Leaderboard: hero row → table header → podium (3 cards, 1st tallest in center) → leaderboard rows
 */
const LeaderboardSkeleton: React.FC<{
  styles: ReturnType<typeof getWorldCupSkeletonStyles>;
}> = ({ styles }) => (
  <View style={styles.container}>
    {/* Avatar stack + title + participant count */}
    <Skeleton width="100%" height={68} borderRadius={12} />
    {/* Column header labels */}
    <Skeleton width="65%" height={14} borderRadius={6} />
    {/* Podium: 2nd | 1st (tallest) | 3rd, aligned at bottom */}
    <View style={styles.podiumRowAligned}>
      <View style={styles.flex1}><Skeleton width="100%" height={130} borderRadius={16} /></View>
      <View style={styles.flex1}><Skeleton width="100%" height={160} borderRadius={16} /></View>
      <View style={styles.flex1}><Skeleton width="100%" height={130} borderRadius={16} /></View>
    </View>
    {/* Leaderboard card rows */}
    <Skeleton width="100%" height={56} borderRadius={16} />
    <Skeleton width="100%" height={56} borderRadius={16} />
    <Skeleton width="100%" height={56} borderRadius={16} />
    <Skeleton width="100%" height={56} borderRadius={16} />
  </View>
);

/**
 * Picks: futures card → Upcoming/Finished tabs → stage label → match cards
 */
const PicksSkeleton: React.FC<{
  styles: ReturnType<typeof getWorldCupSkeletonStyles>;
}> = ({ styles }) => (
  <View style={styles.container}>
    {/* FuturesCard (tournament winner + top scorer picks) */}
    <Skeleton width="100%" height={120} borderRadius={16} />
    {/* Upcoming / Finished filter pills */}
    <View style={styles.pillsRow}>
      <Skeleton width={110} height={34} borderRadius={999} />
      <Skeleton width={110} height={34} borderRadius={999} />
    </View>
    {/* Stage section header label */}
    <View style={styles.sectionLabel}>
      <Skeleton width={90} height={12} borderRadius={6} />
    </View>
    {/* Match cards */}
    <Skeleton width="100%" height={120} borderRadius={16} />
    <Skeleton width="100%" height={120} borderRadius={16} />
    <View style={styles.sectionLabel}>
      <Skeleton width={90} height={12} borderRadius={6} />
    </View>
    <Skeleton width="100%" height={120} borderRadius={16} />
  </View>
);

/**
 * Groups: page title → Create/Join buttons → group cards
 */
const GroupsSkeleton: React.FC<{
  styles: ReturnType<typeof getWorldCupSkeletonStyles>;
}> = ({ styles }) => (
  <View style={styles.container}>
    {/* Page title */}
    <Skeleton width={130} height={28} borderRadius={8} />
    {/* Create / Join CTA buttons */}
    <View style={styles.podiumRow}>
      <View style={styles.flex1}><Skeleton width="100%" height={52} borderRadius={12} /></View>
      <View style={styles.flex1}><Skeleton width="100%" height={52} borderRadius={12} /></View>
    </View>
    {/* Group cards */}
    <Skeleton width="100%" height={120} borderRadius={14} />
    <Skeleton width="100%" height={120} borderRadius={14} />
  </View>
);

/**
 * GroupDetail: hero banner → 3D podium rewards → leaderboard section → table header → rows
 */
const GroupDetailSkeleton: React.FC<{
  styles: ReturnType<typeof getWorldCupSkeletonStyles>;
}> = ({ styles }) => (
  <View style={styles.container}>
          {/* Hero banner (full width, matches GroupHeroBanner) */}
    <Skeleton width="100%" height={BANNER_HEIGHT} borderRadius={12} />
    {/* Podium rewards: 3 columns aligned at bottom (2nd | 1st | 3rd) */}
    <View style={styles.podiumRowAligned}>
      <View style={styles.flex1}><Skeleton width="100%" height={58} borderRadius={6} /></View>
      <View style={styles.flex1}><Skeleton width="100%" height={80} borderRadius={6} /></View>
      <View style={styles.flex1}><Skeleton width="100%" height={46} borderRadius={6} /></View>
    </View>
    {/* "Leaderboard" section label + column header */}
    <View style={styles.sectionLabel}>
      <Skeleton width={100} height={14} borderRadius={6} />
    </View>
    <Skeleton width="70%" height={14} borderRadius={6} />
    {/* Leaderboard card rows */}
    <Skeleton width="100%" height={56} borderRadius={16} />
    <Skeleton width="100%" height={56} borderRadius={16} />
    <Skeleton width="100%" height={56} borderRadius={16} />
    <Skeleton width="100%" height={56} borderRadius={16} />
  </View>
);

const VARIANT_MAP: Record<SkeletonVariant, React.FC<{
  styles: ReturnType<typeof getWorldCupSkeletonStyles>;
}>> = {
  picks: PicksSkeleton,
  leaderboard: LeaderboardSkeleton,
  groups: GroupsSkeleton,
  groupDetail: GroupDetailSkeleton,
};

export const WorldCupSkeleton: React.FC<WorldCupSkeletonProps> = ({ variant }) => {
  const theme = useTheme();
  const { width: windowWidth } = useWindowDimensions();
  const styles = useMemo(() => getWorldCupSkeletonStyles(theme, windowWidth), [theme, windowWidth]);
  const VariantComponent = VARIANT_MAP[variant];
  return <VariantComponent styles={styles} />;
};
